import React, { useEffect, useState } from 'react';
import { Alert, Card, Input, Layout, Table, Tag, Typography, message, Button, Space, Modal, Popconfirm, Select } from 'antd';
import { MailOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { collection, getDocs, orderBy, query, where, getDoc, doc, runTransaction, deleteField } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth } from '../../context/DataContext';
import CustomSeatPicker from '../SelectSeat/CustomSeatPicker';
import dayjs from 'dayjs';
import apis from '../../apis';
import { useTranslation } from 'react-i18next';

const { Content } = Layout;
const { Title, Text } = Typography;

const formatRupiah = (amount) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);

const isMasterclassTicket = (ticket) => ['master_class', 'masterclass'].includes(String(ticket.id || '').toLowerCase());
const isPaidBooking = (booking) => booking.paymentStatus === 'PAID' || booking.paymentStatus === 'paid';
const isCancellationResolved = status => ['canceled', 'manually_verified_no_active_invoice', 'manual_payment_not_received'].includes(status);
const canSafelyDeleteBooking = (booking) =>
    ((booking.paymentStatus === 'expired' && isCancellationResolved(booking.expiry?.invoiceCancellationStatus))
    || (booking.paymentStatus === 'failed' && booking.checkoutFailure?.cleanupStatus === 'complete'))
    && booking.expiry?.quotaRefundStatus !== 'reconciliation_required'
    && booking.checkoutFailure?.quotaRefundStatus !== 'reconciliation_required';
const adminReleaseReasonLabels = {
    customer_declined: 'adminSeatRelease.reasonCustomerDeclined',
    no_response_after_one_hour: 'adminSeatRelease.reasonNoResponse',
};

const normalizeSearch = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
const matchesCustomerSearch = (booking, search) => {
    const searchText = normalizeSearch([
        booking.buyerName, booking.userName, booking.registrantName,
        booking.userEmail, booking.userPhone, booking.id, booking.registrantId,
    ].join(' '));
    return normalizeSearch(search).split(' ').filter(Boolean).every(term => searchText.includes(term));
};

const PublicCustomersList = () => {
    const { t } = useTranslation();
    const { currentEventId } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [venuesMap, setVenuesMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerPage, setCustomerPage] = useState(1);

    // Seat Assignment State
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [seatLayout, setSeatLayout] = useState([]);
    const [loadingSeats, setLoadingSeats] = useState(false);
    const [selectedNewSeats, setSelectedNewSeats] = useState([]);
    const [quantityToAssign, setQuantityToAssign] = useState(0);

    const [eventsList, setEventsList] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');

    useEffect(() => {
        if (currentEventId && !selectedEventId) {
            setSelectedEventId(currentEventId);
        }
    }, [currentEventId, selectedEventId]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const eventsSnap = await getDocs(collection(db, 'events'));
                const eventsData = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                eventsData.sort((a, b) => b.id.localeCompare(a.id));
                setEventsList(eventsData);
            } catch (error) {
                console.error("Error fetching events list:", error);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        setCustomerSearch('');
        setCustomerPage(1);
        if (selectedEventId) {
            fetchCustomers();
        }
    }, [selectedEventId]);

    const fetchCustomers = async () => {
        if (!selectedEventId) return;
        setLoading(true);
        try {
            const q = query(
                collection(db, 'publicBookings'), 
                where('eventId', '==', selectedEventId)
            );
            const querySnapshot = await getDocs(q);
            let data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })).filter(booking => booking.paymentStatus !== 'archived_test');

            // Sort by createdAt desc in memory to avoid requiring a composite index in Firestore
            data.sort((a, b) => {
                const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt || 0);
                const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt || 0);
                return timeB - timeA;
            });

            const eventRef = doc(db, 'events', selectedEventId);
            const eventSnap = await getDoc(eventRef);
            if (eventSnap.exists()) {
                const eventData = eventSnap.data();
                if (eventData.venues) {
                    const vMap = {};
                    eventData.venues.forEach(v => { vMap[v.id] = v.label; });
                    setVenuesMap(vMap);
                }
            }

            setCustomers(data);
        } catch (error) {
            console.error('Error fetching customers:', error);
            message.error('Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    const openAssignModal = async (record) => {
        if (record.seatingMode === 'free') return;
        if (!isPaidBooking(record)) {
            message.error('Seats can only be assigned after payment is confirmed.');
            return;
        }
        const perfTickets = (record.tickets || [])
            .filter(ticket => !isMasterclassTicket(ticket))
            .reduce((total, ticket) => total + Number(ticket.quantity || 0), 0);
        const assignedCount = (record.selectedSeatIds || []).length;
        const toAssign = perfTickets - assignedCount;

        if (toAssign <= 0) {
            message.info('All seats are already assigned for this booking.');
            return;
        }

        setSelectedBooking(record);
        setQuantityToAssign(toAssign);
        setSelectedNewSeats([]);
        setIsAssignModalOpen(true);
        setLoadingSeats(true);
        setSeatLayout([]);

        try {
            const sessionId = `${record.date}_${record.session}`;
            const seatsCollection = collection(db, `seats${selectedEventId || 'APCS2026'}`);
            const q = query(seatsCollection, where('sessionId', '==', sessionId), where('venueId', '==', record.venue));
            const snapshot = await getDocs(q);
            
            const fetchedSeats = [];
            snapshot.forEach(docSnap => {
                fetchedSeats.push({ id: docSnap.id, ...docSnap.data() });
            });

            // Format layout
            const groupedByArea = fetchedSeats.reduce((acc, seat) => {
                const area = seat.areaType?.toLowerCase();
                if (!acc[area]) acc[area] = [];
                acc[area].push(seat);
                return acc;
            }, {});

            const finalLayouts = {};
            const areaOrder = ['presto', 'allegro', 'lento'];
            const rightSideMax = {
                A: 9, B: 10, C: 10, D: 9, E: 9, F: 11,
                G: 9, H: 9, I: 9, J: 8, K: 8, L: 9, M: 10,
                N: 13, O: 13, P: 13, Q: 13, R: 12, S: 12, T: 12, U: 12
            };

            const areasInDb = Object.keys(groupedByArea);
            const sortedAreas = areasInDb.sort((a, b) => {
                const idxA = areaOrder.indexOf(a.toLowerCase());
                const idxB = areaOrder.indexOf(b.toLowerCase());
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return a.localeCompare(b);
            });

            sortedAreas.forEach(area => {
                if (groupedByArea[area]) {
                    const rowsObject = groupedByArea[area].reduce((acc, seat) => {
                        const row = seat.row;
                        if (!acc[row]) acc[row] = [];
                        acc[row].push(seat);
                        return acc;
                    }, {});

                    const sortedRowKeys = Object.keys(rowsObject).sort((a, b) => a.localeCompare(b));

                    finalLayouts[area] = sortedRowKeys.map(rowKey => {
                        const rowSeats = rowsObject[rowKey];
                        rowSeats.sort((a, b) => b.number - a.number);
                        
                        const mappedRow = rowSeats.map(seat => {
                            let tooltipText = `Status: ${seat.status.toUpperCase()}`;
                            if (seat.status === 'booked' && seat.assignedTo) {
                                tooltipText = `Booked by: ${seat.assignedTo.registrantName || seat.assignedTo.userName} (${seat.assignedTo.userEmail || ''})`;
                            }
                            
                            return {
                                id: seat.id,
                                number: seat.number,
                                row: seat.row,
                                isReserved: seat.status !== 'available',
                                status: seat.status,
                                tooltip: (
                                    <div style={{ textAlign: 'center' }}>
                                        <strong style={{ display: 'block', marginBottom: '4px' }}>Seat {seat.seatLabel}</strong>
                                        <span>{tooltipText}</span>
                                    </div>
                                ),
                                statusColor: seat.status === 'booked' ? '#ff4d4f' : seat.status === 'reserved' ? '#bfbfbf' : seat.status === 'locked' ? '#faad14' : undefined,
                                seatLabel: seat.seatLabel
                            };
                        });

                        if (record.venue === 'Venue1') {
                            const splitSeatNumber = rightSideMax[rowKey];
                            if (splitSeatNumber) {
                                const splitIndex = mappedRow.findIndex(s => s.number === splitSeatNumber);
                                if (splitIndex !== -1) {
                                    mappedRow.splice(splitIndex, 0, null, null);
                                }
                            }
                        }

                        return mappedRow;
                    });
                }
            });

            const finalLayoutArray = [];
            areaOrder.forEach(area => {
                if (finalLayouts[area]) {
                    finalLayoutArray.push(...finalLayouts[area]);
                }
            });

            setSeatLayout(finalLayoutArray);

        } catch (error) {
            console.error("Error fetching seat layout:", error);
            message.error("Failed to load seat map.");
        } finally {
            setLoadingSeats(false);
        }
    };

    const handleSelectSeat = (seat) => {
        setSelectedNewSeats(prev => [...prev, seat]);
    };

    const handleDeselectSeat = (seat) => {
        setSelectedNewSeats(prev => prev.filter(s => s.id !== seat.id));
    };

    const handleAssignSeatsSubmit = async () => {
        if (selectedNewSeats.length !== quantityToAssign) {
            message.error(`Please select exactly ${quantityToAssign} seats.`);
            return;
        }

        setLoading(true);
        try {
            // The protected backend transaction validates payment, session, tier, quantity,
            // canonical chair ownership and physical aliases before booking the seats.
            await apis.publicTicket.assignSeats(selectedBooking.id, selectedNewSeats.map(seat => seat.id));

            message.success('Seats successfully assigned!');
            setIsAssignModalOpen(false);
            fetchCustomers();
        } catch (error) {
            console.error("Assignment error:", error);
            message.error(`Failed to assign seats: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleResendEmail = async (bookingId) => {
        try {
            message.loading({ content: 'Resending email...', key: 'resendEmail' });
            const response = await apis.publicTicket.resendPublicTicketEmail({ bookingId });
            if (response.error) throw new Error(response.error.data?.message || 'Failed to resend email.');
            message.success({ content: 'Email resent successfully!', key: 'resendEmail' });
            
            // Update the local state to show email as sent
            setCustomers(prev => prev.map(c => c.id === bookingId ? { ...c, emailSent: true } : c));
        } catch (error) {
            console.error('Error resending email:', error);
            message.error({ content: error.response?.data?.message || 'Failed to resend email.', key: 'resendEmail' });
        }
    };

    const handleMarkPaid = async (record) => {
        try {
            message.loading({ content: 'Marking as paid...', key: 'markPaid' });
            if (record.paymentMode === 'manual') {
                const token = await auth.currentUser?.getIdToken();
                if (!token) throw new Error('Please sign in again.');
                const response = await apis.publicTicket.markManualPaid(record.id, token);
                if (response.error) throw new Error(response.error.data?.message || 'Could not confirm manual payment.');
                message.success({ content: 'Manual payment confirmed. Confirmation email attempted.', key: 'markPaid' });
                await fetchCustomers();
                return;
            }
            
            await runTransaction(db, async (transaction) => {
                const bookingRef = doc(db, 'publicBookings', record.id);
                const bookingDoc = await transaction.get(bookingRef);
                
                if (!bookingDoc.exists()) {
                    throw new Error("Booking does not exist.");
                }

                const bookingData = bookingDoc.data();

                if (bookingData.paymentStatus !== 'pending') {
                    throw new Error('This booking cannot be marked paid unless it is currently pending and payment has been verified.');
                }
                if (!bookingData.eventId) throw new Error('Booking has no event reference.');

                // Collect all seat IDs
                const seatIdsToReserve = [...new Set([
                    ...(bookingData.selectedSeatIds || []),
                    ...(bookingData.orchestraSelectedSeatIds || [])
                ])];

                // Retrieve all seat docs
                const seatDocs = [];
                for (const seatId of seatIdsToReserve) {
                    const seatRef = doc(db, `seats${bookingData.eventId}`, seatId);
                    const seatSnap = await transaction.get(seatRef);
                    if (!seatSnap.exists()) throw new Error(`Seat ${seatId} no longer exists.`);
                    const seatData = seatSnap.data();
                    if (seatData.status !== 'locked' || seatData.lockedByBookingId !== record.id) {
                        throw new Error(`Seat ${seatData.seatLabel || seatId} is no longer locked by this booking.`);
                    }
                    seatDocs.push(seatSnap);
                }
                const normalizeSeatPart = value => String(value ?? '').trim().toUpperCase();
                const safeDocumentId = value => encodeURIComponent(String(value ?? '')).replace(/%/g, '_');
                const physicalSeatKey = seat => [
                    bookingData.eventId,
                    normalizeSeatPart(seat.venueId),
                    normalizeSeatPart(seat.sessionId),
                    normalizeSeatPart(seat.row),
                    String(seat.number ?? '').trim(),
                ].join('|');
                const physicalSeatKeys = seatDocs.map(seatSnap => physicalSeatKey(seatSnap.data()));
                if (new Set(physicalSeatKeys).size !== physicalSeatKeys.length) {
                    throw new Error('A physical seat can only be finalized once per booking.');
                }
                const ownershipRefs = physicalSeatKeys.map(key =>
                    doc(db, 'ticketSeatOwnership', safeDocumentId(key)));
                const ownershipDocs = [];
                for (const ownershipRef of ownershipRefs) {
                    ownershipDocs.push(await transaction.get(ownershipRef));
                }
                for (let index = 0; index < ownershipDocs.length; index++) {
                    const ownership = ownershipDocs[index];
                    if (ownership.exists() && ownership.data().active !== false
                        && ownership.data().bookingId !== record.id) {
                        throw new Error(`Physical seat ${seatDocs[index].data().seatLabel} is already held by another booking.`);
                    }
                }

                // Update seats
                for (let index = 0; index < seatDocs.length; index++) {
                    const seatSnap = seatDocs[index];
                    transaction.update(seatSnap.ref, {
                        status: 'booked',
                        bookingId: record.id,
                        lockedAt: deleteField(),
                        lockedByBookingId: deleteField(),
                        assignedTo: {
                            userName: bookingData.userName || '',
                            userEmail: bookingData.userEmail || '',
                            registrantName: bookingData.registrantName || ''
                        }
                    });
                    transaction.set(ownershipRefs[index], {
                        eventId: bookingData.eventId,
                        physicalSeatKey: physicalSeatKeys[index],
                        bookingId: record.id,
                        seatId: seatSnap.id,
                        status: 'booked',
                        active: true,
                        updatedAt: new Date(),
                    });
                }

                // Update booking
                transaction.update(bookingRef, {
                    paymentStatus: 'PAID',
                    updatedAt: new Date()
                });
            });

            message.success({ content: 'Marked as PAID successfully! You can now resend the email.', key: 'markPaid' });
            setCustomers(prev => prev.map(c => c.id === record.id ? { ...c, paymentStatus: 'PAID' } : c));
        } catch (error) {
            console.error('Error marking as paid:', error);
            message.error({ content: error.message || 'Failed to mark as paid', key: 'markPaid' });
        }
    };

    const handleCancelManualBooking = async (record) => {
        try {
            const token = await auth.currentUser?.getIdToken();
            if (!token) throw new Error('Please sign in again.');
            const response = await apis.publicTicket.releaseBooking({
                bookingId: record.id,
                reason: 'manual_payment_unpaid',
                note: 'Staff verified that payment was not received.',
                paymentNotReceivedConfirmed: true,
            }, token);
            if (response.error || !response.data?.released) {
                throw new Error(response.error?.data?.message || 'Could not cancel manual booking.');
            }
            message.success('Unpaid manual booking canceled and inventory released.');
            await fetchCustomers();
        } catch (error) {
            message.error(error.message || 'Could not cancel manual booking.');
        }
    };

    const handleResendManualInstructions = async (record) => {
        try {
            const token = await auth.currentUser?.getIdToken();
            if (!token) throw new Error('Please sign in again.');
            const response = await apis.publicTicket.resendManualInstructions(record.id, token);
            if (response.error || !response.data?.sent) {
                throw new Error(response.error?.data?.message || 'Could not send payment instructions.');
            }
            message.success('Payment instructions sent.');
            setCustomers(previous => previous.map(customer => customer.id === record.id
                ? { ...customer, manualInstructionsEmailSent: true } : customer));
        } catch (error) {
            message.error(error.message || 'Could not send payment instructions.');
        }
    };

    const handleDeleteBooking = async (bookingId) => {
        try {
            message.loading({ content: 'Deleting booking...', key: 'deleteBooking' });
            await runTransaction(db, async (transaction) => {
                const bookingRef = doc(db, 'publicBookings', bookingId);
                const bookingDoc = await transaction.get(bookingRef);
                
                if (!bookingDoc.exists()) {
                    throw new Error("Booking does not exist.");
                }

                const bookingData = bookingDoc.data();
                if (!canSafelyDeleteBooking(bookingData)) {
                    throw new Error('This booking still has payment or inventory state. Reconcile it through the cancellation process instead of deleting it.');
                }

                // Inventory and quota were already released by the confirmed cancellation cleanup.
                transaction.delete(bookingRef);
            });

            message.success({ content: 'Booking deleted successfully', key: 'deleteBooking' });
            fetchCustomers();
        } catch (error) {
            console.error('Error deleting booking:', error);
            message.error({ content: error.message || 'Failed to delete booking', key: 'deleteBooking' });
        }
    };

    const columns = [
        {
            title: 'Booking ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Name',
            dataIndex: 'userName',
            key: 'userName',
        },
        {
            title: 'Email',
            dataIndex: 'userEmail',
            key: 'userEmail',
        },
        {
            title: 'Phone',
            dataIndex: 'userPhone',
            key: 'userPhone',
        },
        {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
            render: (venue) => venuesMap[venue] || 'APCS Venue', 
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
        },
        {
            title: 'Session',
            dataIndex: 'session',
            key: 'session',
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amount) => formatRupiah(amount),
        },
        {
            title: 'Payment Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status, record) => {
                let color = 'default';
                if (status === 'PAID' || status === 'paid') color = 'green';
                else if (status === 'pending') color = 'orange';
                else if (status === 'expired') color = 'red';
                else if (status === 'failed') color = 'red';
                const receiptStatus = !isPaidBooking(record) ? record.paymentReceipt?.status : null;

                return (
                    <Space direction="vertical" size={4}>
                        <Tag color={color}>
                            {status ? status.toUpperCase() : 'UNKNOWN'}
                        </Tag>
                        {receiptStatus === 'pending' && (
                            <Tag color="gold">Paid callback received — reconciling</Tag>
                        )}
                        {receiptStatus === 'needs_review' && (
                            <Tag color="volcano">Payment received — needs review ({record.paymentReceipt.reason})</Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Seats',
            dataIndex: 'performanceSeatLabels',
            key: 'performanceSeatLabels',
            render: (seats) => (seats && seats.length > 0) ? seats.join(', ') : '-',
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (timestamp) => timestamp ? dayjs(timestamp.toDate()).format('DD MMM YYYY HH:mm') : '-',
        },
        {
            title: 'Email Status',
            dataIndex: 'emailSent',
            key: 'emailSent',
            render: (emailSent, record) => {
                if (record.paymentMode === 'manual' && record.paymentStatus === 'pending') {
                    return record.manualInstructionsEmailSent
                        ? <Tag color="blue">PAYMENT INFO SENT</Tag>
                        : <Tag color="orange">PAYMENT INFO NEEDS SENDING</Tag>;
                }
                if (emailSent) return <Tag color="green">SENT</Tag>;
                return <Tag color="red">PENDING/FAILED</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        type="primary" 
                        icon={<MailOutlined />} 
                        size="small" 
                        onClick={() => handleResendEmail(record.id)}
                        disabled={record.paymentStatus !== 'PAID' && record.paymentStatus !== 'paid'}
                    >
                        Resend
                    </Button>
                    {record.paymentStatus === 'pending' && (
                        <Popconfirm
                            title="Mark as Paid?"
                            description={record.paymentMode === 'manual'
                                ? 'Confirm that staff verified payment. This will finalize the booking and attempt its confirmation email.'
                                : 'This will confirm the booking and assign the seats. Continue?'}
                            onConfirm={() => handleMarkPaid(record)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button 
                                type="default" 
                                icon={<CheckCircleOutlined />} 
                                size="small" 
                                style={{ color: '#52c41a', borderColor: '#52c41a' }}
                            >
                                Mark Paid
                            </Button>
                        </Popconfirm>
                    )}
                    {record.paymentStatus === 'pending' && record.paymentMode === 'manual' && (
                        <Button size="small" onClick={() => handleResendManualInstructions(record)}>
                            {record.manualInstructionsEmailSent ? 'Resend Payment Info' : 'Send Payment Info'}
                        </Button>
                    )}
                    {record.paymentStatus === 'pending' && record.paymentMode === 'manual' && (
                        <Popconfirm
                            title="Cancel unpaid manual booking?"
                            description="Confirm that staff checked payment and no payment was received. This releases all reserved places."
                            onConfirm={() => handleCancelManualBooking(record)}
                            okText="Cancel booking"
                            cancelText="Keep booking"
                        >
                            <Button size="small" danger>Cancel Unpaid</Button>
                        </Popconfirm>
                    )}
                    {canSafelyDeleteBooking(record) && (
                        <Popconfirm
                            title="Delete reconciled booking?"
                            description="Inventory and complimentary quota were already released after confirmed cancellation."
                            onConfirm={() => handleDeleteBooking(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                            >
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        }
    ];

    const expandedRowRender = (record) => {
        return (
            <div style={{ margin: '0 24px', padding: '16px', background: '#f9f9f9', borderRadius: '8px' }}>
                <Title level={5} style={{ marginTop: 0 }}>Order Details</Title>
                {record.paymentStatus === 'failed' && (
                    <div style={{ marginBottom: 16 }}>
                        <Text type="danger">Checkout failed. This booking cannot be marked paid.</Text>
                        <br />
                        <Text>Invoice cancellation: {{ canceled: 'Confirmed', pending: 'Pending', failed: 'Needs reconciliation', unknown: 'Unknown — needs reconciliation', not_requested: 'Invoice not requested', manually_verified_no_active_invoice: t('adminSeatRelease.manualNoInvoiceStatus') }[record.checkoutFailure?.invoiceCancellationStatus] || 'Unknown — needs reconciliation'}</Text>
                        {record.checkoutFailure?.quotaRefundStatus === 'reconciliation_required' && (
                            <p><Text type="danger">Complimentary quota requires reconciliation.</Text></p>
                        )}
                    </div>
                )}
                {record.adminRelease && (
                    <Alert
                        style={{
                            marginBottom: 16,
                            background: '#151515',
                            borderColor: '#c8a95b',
                            color: '#f7f0df',
                        }}
                        type="success"
                        showIcon
                        icon={<CheckCircleOutlined style={{ color: '#c8a95b' }} />}
                        message={<span style={{ color: '#e7cc83' }}>{t('adminSeatRelease.publicStatusTitle')}</span>}
                        description={(
                            <div style={{ color: '#f7f0df' }}>
                                <div>{t('adminSeatRelease.publicReason')}: {adminReleaseReasonLabels[record.adminRelease.reason] ? t(adminReleaseReasonLabels[record.adminRelease.reason]) : record.adminRelease.reason || '-'}</div>
                                <div>{t('adminSeatRelease.publicReleasedBy')}: {record.adminRelease.releasedByEmail || '-'}</div>
                                {record.adminRelease.note && <div>{t('adminSeatRelease.publicNote')}: {record.adminRelease.note}</div>}
                            </div>
                        )}
                    />
                )}
                <div style={{ display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: '200px' }}>
                        <Text strong>Tickets:</Text>
                        <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                            {(record.tickets || []).filter(t => t.quantity > 0).map((t, idx) => (
                                <li key={idx}>{t.quantity}x {t.name}</li>
                            ))}
                            {(!record.tickets || record.tickets.filter(t => t.quantity > 0).length === 0) && (
                                <li>None</li>
                            )}
                        </ul>
                    </div>

                    {(record.addOnIds?.length > 0 || record.freeMasterclassCount > 0) && (
                        <div style={{ minWidth: '200px' }}>
                            <Text strong>Add-ons & Extras:</Text>
                            <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                                {(() => {
                                    if (!record.addOnIds) return null;
                                    const counts = {};
                                    record.addOnIds.forEach(id => { counts[id] = (counts[id] || 0) + 1; });
                                    return Object.entries(counts).map(([id, qty]) => (
                                        <li key={id}>{qty}x {id.replace(/_/g, ' ')}</li>
                                    ));
                                })()}
                                {record.freeMasterclassCount > 0 && (
                                    <li>{record.freeMasterclassCount}x Master Class (Free)</li>
                                )}
                            </ul>
                        </div>
                    )}

                    <div style={{ minWidth: '200px' }}>
                        <Text strong>Seats & Assignments:</Text>
                        <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                            <li>
                                <strong>Performance:</strong> {(record.performanceSeatLabels || []).join(', ') || '-'}
                            </li>
                            {(record.orchestraSeatLabels?.length > 0 || record.complimentaryTickets > 0) && (
                                <li>
                                    <strong>Orchestra (Free):</strong> {(record.orchestraSeatLabels || []).join(', ') || 'Auto-Assigned'}
                                </li>
                            )}
                            {record.ticketingVersion === 2 && record.orchestraAttendanceTickets > 0 && <li><strong>Orchestra:</strong> {record.orchestraAttendanceTickets} ticket(s); performer places counted once across purchases. Manage the whole group in Orchestra Assignments.</li>}
                            {record.registrantName && (
                                <li>
                                    <strong>Paying For:</strong> {record.registrantName}
                                </li>
                            )}
                        </ul>
                        {(() => {
                            if (record.seatingMode === 'free') return <Tag color="gold">Free seating</Tag>;
                            const perfTickets = (record.tickets || [])
                                .filter(ticket => !isMasterclassTicket(ticket))
                                .reduce((acc, t) => acc + parseInt(t.quantity || 0, 10), 0);
                            const assignedCount = (record.selectedSeatIds || []).length;
                            const isPaid = record.paymentStatus === 'PAID' || record.paymentStatus === 'paid';
                            
                            if (perfTickets > assignedCount && isPaid) {
                                return (
                                    <Button 
                                        type="primary" 
                                        size="small" 
                                        style={{ marginTop: '16px' }}
                                        onClick={() => openAssignModal(record)}
                                    >
                                        Assign Missing Seats ({perfTickets - assignedCount})
                                    </Button>
                                );
                            } else if (perfTickets > assignedCount && !isPaid) {
                                return (
                                    <div style={{ marginTop: '16px', color: '#faad14' }}>
                                        <i>Cannot assign seats (Payment Status is {record.paymentStatus || 'unknown'})</i>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                    </div>
                </div>
            </div>
        );
    };

    const filteredCustomers = customers.filter(booking => matchesCustomerSearch(booking, customerSearch));

    return (
        <Content style={{ padding: '24px', minHeight: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2}>Public Ticket Bookings</Title>
                    <Text type="secondary">List of all public ticket bookings and their payment status for the selected event.</Text>
                </div>
                <div>
                    <span style={{ marginRight: '8px', fontWeight: 500 }}>Filter by Event:</span>
                    <Select 
                        value={selectedEventId} 
                        onChange={(value) => {
                            setSelectedEventId(value);
                            setCustomers([]);
                        }} 
                        style={{ width: 200 }}
                    >
                        {eventsList.map(ev => (
                            <Select.Option key={ev.id} value={ev.id}>{ev.title || ev.id}</Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            <Card style={{ marginTop: '24px' }}>
                <Input
                    aria-label="Search public ticket bookings"
                    placeholder="Search reservation or performer name, email, phone or ID"
                    allowClear
                    value={customerSearch}
                    onChange={event => {
                        setCustomerSearch(event.target.value);
                        setCustomerPage(1);
                    }}
                    style={{ width: 480, maxWidth: '100%', marginBottom: 16 }}
                />
                <Table
                    columns={columns}
                    dataSource={filteredCustomers}
                    rowKey="id"
                    loading={loading}
                    pagination={{ current: customerPage, pageSize: 10, onChange: setCustomerPage, showSizeChanger: false }}
                    locale={{ emptyText: customerSearch.trim() ? 'No bookings match your search.' : 'No bookings for the selected event.' }}
                    scroll={{ x: 1200 }}
                    expandable={{ expandedRowRender }}
                />
            </Card>

            <Modal
                title={`Assign Seats for ${selectedBooking?.buyerName || selectedBooking?.userName}`}
                open={isAssignModalOpen}
                onCancel={() => setIsAssignModalOpen(false)}
                width={1000}
                onOk={handleAssignSeatsSubmit}
                okText="Save Assignment"
                confirmLoading={loading}
                okButtonProps={{ disabled: selectedNewSeats.length !== quantityToAssign }}
                destroyOnClose
            >
                <div style={{ marginBottom: '16px' }}>
                    <Text>
                        Please select <strong>{quantityToAssign}</strong> seats. 
                        Currently selected: {selectedNewSeats.length}
                    </Text>
                    {selectedBooking && (
                        <div style={{ marginTop: '8px' }}>
                            <Text type="secondary">
                                Note: This user purchased: {(selectedBooking.tickets || []).filter(ticket => !isMasterclassTicket(ticket) && ticket.quantity > 0).map(ticket => `${ticket.quantity}x ${ticket.name}`).join(', ')}.
                                Please select seats corresponding to their purchased tier.
                            </Text>
                        </div>
                    )}
                </div>
                <div style={{ overflowX: 'auto', backgroundColor: '#f0f2f5', padding: '24px', borderRadius: '8px' }}>
                    {loadingSeats ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>Loading Seat Map...</div>
                    ) : (
                        <CustomSeatPicker 
                            layout={seatLayout} 
                            selectedSeats={selectedNewSeats} 
                            onSelect={handleSelectSeat} 
                            onDeselect={handleDeselectSeat} 
                            maxSeats={quantityToAssign} 
                        />
                    )}
                </div>
            </Modal>
        </Content>
    );
};

export default PublicCustomersList;
